import { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { Button } from '../ui/button';
import { X, ChevronRight, ChevronLeft, CheckCircle, Palette, Code, Users, Rocket } from 'lucide-react';

interface OnboardingWizardProps {
  customerId: string;
  customerSlug: string;
  onComplete: () => void;
  onSkip: () => void;
}

export default function OnboardingWizard({
  customerId,
  customerSlug,
  onComplete,
  onSkip,
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: Palette,
      title: 'Customize Your Branding',
      description: 'Make your feedback portal truly yours by uploading your logo, setting brand colors, and customizing the banner.',
      action: 'Click "Branding" tab above',
      tips: [
        'Upload your company logo and favicon',
        'Set your primary brand color',
        'Customize the banner title and description',
      ],
    },
    {
      icon: Code,
      title: 'Get Your Integration URL',
      description: 'Share your feedback portal with users. You can embed it in your app or share the link directly.',
      action: `Portal URL: getproductworkshop.com/${customerSlug}`,
      tips: [
        'Share this URL with your users',
        'Add it to your app\'s help menu',
        'Include it in your documentation',
      ],
    },
    {
      icon: Users,
      title: 'Invite Your Team',
      description: 'Add team members to help you manage feedback and collaborate on feature decisions.',
      action: 'Coming soon: Team Management',
      tips: [
        'Invite colleagues to admin portal',
        'Assign different permission levels',
        'Collaborate on request responses',
      ],
    },
    {
      icon: Rocket,
      title: 'You\'re All Set!',
      description: 'Your feedback portal is ready to go. Start collecting valuable insights from your users.',
      action: 'Start using ProductWorkshop',
      tips: [
        'Users can submit feature requests',
        'Vote on popular requests',
        'Comment and discuss ideas',
        'Track everything in your admin dashboard',
      ],
    },
  ];

  const handleComplete = async () => {
    try {
      await updateDoc(doc(db, 'customers', customerId), {
        onboardingCompleted: true,
        onboardingCompletedAt: new Date(),
      });
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      onComplete(); // Complete anyway
    }
  };

  const handleSkip = async () => {
    try {
      await updateDoc(doc(db, 'customers', customerId), {
        onboardingSkipped: true,
      });
      onSkip();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      onSkip(); // Skip anyway
    }
  };

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full shadow-2xl">
        {/* Header */}
        <div className="border-b border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-foreground">
              Welcome to Your Admin Portal! 🎉
            </h2>
            <button
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Progress Bar */}
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Icon className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">
              {currentStepData.title}
            </h3>
            <p className="text-muted-foreground max-w-md">
              {currentStepData.description}
            </p>
          </div>

          {/* Action */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium text-primary text-center">
              {currentStepData.action}
            </p>
          </div>

          {/* Tips */}
          <div className="space-y-3">
            {currentStepData.tips.map((tip, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">{tip}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Step {currentStep + 1} of {steps.length}
          </div>
          <div className="flex gap-3">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <Button variant="ghost" onClick={handleSkip}>
              Skip for now
            </Button>
            {isLastStep ? (
              <Button onClick={handleComplete} className="bg-primary text-white">
                Get Started
                <Rocket className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={() => setCurrentStep(currentStep + 1)} className="bg-primary text-white">
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
